import { useState, useCallback, useEffect, useMemo, useRef, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import * as maplibregl from 'maplibre-gl';
import type { Feature } from 'geojson';
import { useAppAuth } from '../hooks/useAppAuth';
import { MAP_STYLE_URL, createPinElement } from '../lib/mapLibre';
import {
  MapPin, Clock, Truck, ChevronRight, ChevronDown, ChevronUp, Star, Zap,
  SlidersHorizontal, ArrowUpDown, X, RotateCcw, Pencil,
  Heart, Check, XCircle, ChevronsDown, ChevronsUp, Filter,
  Users, Link2, Copy, CheckCircle, Map as MapIcon, Navigation2, Lock,
} from 'lucide-react';
import { useRefresh } from '../hooks/useRefresh';
import { RefreshButton } from '../components/RefreshButton';
import { RouteMap, RelayMap } from '../components/RelayMap';
import { jobService, quoteService, profileService, createApiClient, messagesApi, carrierEmployeesService, vehiclesService, jobAssignmentService, eligibilityService, favouriteJobsService, jobAlertsService, platformFeeConfigService } from '../services/api';
import type { QuoteEligibilityResult } from '../services/api';
import { UserAvatar } from '../components/UserAvatar';
import { MessagingPanel } from '../components/MessagingPanel';
import { useJobHub, useFleetHub, type FleetLocationUpdate } from '../hooks/useSignalR';
import type { JobSummaryDto, QuoteSummaryDto, QuoteReceivedEvent, ContractGeneratedEvent, JobAssignment } from '../types';
import { routeZoneLabels } from '../types';
import { JobStatus, JobType, PaymentTerm, QuoteStatus, VehicleType } from '../types';

// 
//  CONSTANTS
// 
const JOB_TYPE_LABELS: Record<JobType, string> = {
  [JobType.SameDayDelivery]:         'Same-Day Delivery',
  [JobType.ScheduledDelivery]:       'Scheduled Delivery',
  [JobType.RelayDelivery]:           'Relay Delivery',
  [JobType.VehicleRepair]:           'Vehicle Repair',
  [JobType.Removal]:                 'Removal',
  [JobType.FreightHaulage]:          'Freight Haulage',
  [JobType.MultiDropDelivery]:       'Multi-Drop',
  [JobType.MultiPickupDropDelivery]: 'Multi-Pickup-Drop',
};

const VEHICLE_LABELS: Record<VehicleType, string> = {
  [VehicleType.Motorcycle]: 'Motorcycle',
  [VehicleType.Car]:        'Car',
  [VehicleType.SmallVan]:   'Small Van',
  [VehicleType.MidiVan]:    'Midi Van',
  [VehicleType.LWBVan]:     'LWB Van',
  [VehicleType.LutonVan]:   'Luton Van',
  [VehicleType.Tonne7_5]:   '7.5T',
  [VehicleType.Tonne12]:    '12T',
  [VehicleType.Tonne18]:    '18T',
  [VehicleType.Tonne26]:    '26T',
  [VehicleType.Artic]:      'Artic',
};

const VEHICLE_ICONS: Record<VehicleType, string> = {
  [VehicleType.Motorcycle]: '🏍️',
  [VehicleType.Car]:        '🚗',
  [VehicleType.SmallVan]:   '🚐',
  [VehicleType.MidiVan]:    '🚐',
  [VehicleType.LWBVan]:     '🚚',
  [VehicleType.LutonVan]:   '🚚',
  [VehicleType.Tonne7_5]:   '🚛',
  [VehicleType.Tonne12]:    '🚛',
  [VehicleType.Tonne18]:    '🚛',
  [VehicleType.Tonne26]:    '🚛',
  [VehicleType.Artic]:      '🚛',
};

const STATUS_COLOURS: Partial<Record<JobStatus, string>> = {
  [JobStatus.PendingReview]:     'bg-yellow-100 text-yellow-800',
  [JobStatus.Active]:            'bg-green-100 text-green-800',
  [JobStatus.Quoted]:            'bg-blue-100 text-blue-800',
  [JobStatus.Accepted]:          'bg-purple-100 text-purple-800',
  [JobStatus.ContractGenerated]: 'bg-indigo-100 text-indigo-800',
  [JobStatus.InProgress]:        'bg-orange-100 text-orange-800',
  [JobStatus.Completed]:         'bg-gray-100 text-gray-800',
  [JobStatus.PaymentReleased]:   'bg-emerald-100 text-emerald-800',
  [JobStatus.Cancelled]:         'bg-red-100 text-red-800',
  [JobStatus.CarrierAccepted]:    'bg-teal-100 text-teal-800',
};

// 
//  SORT / FILTER TYPES
// 
type SortKey =
  | 'distance-asc'   // nearest pickup first  (default)
  | 'distance-desc'  // furthest pickup first
  | 'newest'         // most recently posted
  | 'oldest'         // posted longest ago
  | 'soonest'        // scheduled soonest
  | 'latest-sched'   // scheduled furthest in future
  | 'floor-desc'     // highest minimum floor (premium)
  | 'floor-asc'      // lowest minimum floor  (entry-level)
  | 'bids-asc'       // fewest bids (least competition)
  | 'bids-desc'      // most bids  (popular / verified)
  | 'route-asc'      // shortest job route
  | 'route-desc';    // longest job route

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'distance-asc',  label: '" Nearest pickup first'    },
  { value: 'distance-desc', label: '" Furthest pickup first'   },
  { value: 'newest',        label: ' Newest posted'           },
  { value: 'oldest',        label: ' Oldest posted'           },
  { value: 'soonest',       label: '"… Soonest scheduled'       },
  { value: 'latest-sched',  label: '"… Latest scheduled'        },
  { value: 'bids-asc',      label: ' Fewest bids (best opportunity)' },
  { value: 'bids-desc',     label: ' Most bids (popular)'    },
  { value: 'route-asc',     label: ' Shortest route'          },
  { value: 'route-desc',    label: ' Longest route'           },
];

function sortJobs(jobs: JobSummaryDto[], key: SortKey): JobSummaryDto[] {
  return [...jobs].sort((a, b) => {
    switch (key) {
      case 'distance-asc':  return (a.distanceFromCarrierMiles ?? 0) - (b.distanceFromCarrierMiles ?? 0);
      case 'distance-desc': return (b.distanceFromCarrierMiles ?? 0) - (a.distanceFromCarrierMiles ?? 0);
      case 'newest':        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      case 'oldest':        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      case 'soonest':       return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
      case 'latest-sched':  return new Date(b.scheduledAt).getTime() - new Date(a.scheduledAt).getTime();
      case 'bids-asc':      return a.quoteCount - b.quoteCount;
      case 'bids-desc':     return b.quoteCount - a.quoteCount;
      case 'route-asc':     return a.distanceMiles - b.distanceMiles;
      case 'route-desc':    return b.distanceMiles - a.distanceMiles;
      default:              return 0;
    }
  });
}

//
//  MAP CONSTANTS + COMPONENT
//

const JOB_COLOURS = [
  '#ef4444','#3b82f6','#22c55e','#f59e0b','#8b5cf6',
  '#ec4899','#06b6d4','#84cc16','#f97316','#6366f1',
  '#14b8a6','#f43f5e','#0ea5e9','#a3e635','#fb923c',
  '#a855f7','#10b981','#fbbf24','#64748b','#e11d48',
  '#0284c7','#16a34a','#d97706','#7c3aed','#0f766e',
];

interface LiveVehicle {
  carrierId:   string;
  carrierName: string;
  jobId?:      string | null;
  lat:         number;
  lng:         number;
  recordedAt:  string;
}

const JOB_ROUTES_SOURCE = 'job-feed-routes';
const JOB_ROUTES_LAYER = 'job-feed-routes-line';

function JobsFullMap({
  jobs,
  vehicles,
  hoveredJobId,
  onJobHover,
}: {
  jobs:         JobSummaryDto[];
  vehicles:     LiveVehicle[];
  hoveredJobId: string | null;
  onJobHover:   (id: string | null) => void;
}) {
  const containerRef   = useRef<HTMLDivElement>(null);
  const mapRef         = useRef<maplibregl.Map | null>(null);
  const markersRef     = useRef<Map<string, { pMark: maplibregl.Marker; dMark: maplibregl.Marker }>>(new Map());
  const pinnedRef      = useRef<Set<string>>(new Set());
  const vehicleMarkRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  const onHoverRef     = useRef(onJobHover);
  const [pinnedCount, setPinnedCount] = useState(0);
  const [mapReady, setMapReady]       = useState(false);

  useEffect(() => { onHoverRef.current = onJobHover; }, [onJobHover]);

  // Tracks the currently-hovered job id without forcing this whole map to re-render on hover -
  // repaintRoute() below reads it synchronously when re-painting a single feature's state.
  const hoveredJobIdLive = useRef<string | null>(null);

  const isHighlighted = useCallback(
    (jobId: string) => pinnedRef.current.has(jobId) || jobId === hoveredJobIdLive.current,
    []);

  const repaintRoute = useCallback((jobId: string) => {
    const map = mapRef.current;
    if (!map || !map.getLayer(JOB_ROUTES_LAYER)) return;
    map.setFeatureState({ source: JOB_ROUTES_SOURCE, id: jobId }, { highlighted: isHighlighted(jobId) });
  }, [isHighlighted]);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: [-1.5, 52.5],
      zoom: 6,
      doubleClickZoom: false,
    });
    mapRef.current = map;
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }));

    map.on('load', () => {
      map.addSource(JOB_ROUTES_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addLayer({
        id: JOB_ROUTES_LAYER, type: 'line', source: JOB_ROUTES_SOURCE,
        layout: { 'line-cap': 'round' },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': ['case', ['boolean', ['feature-state', 'highlighted'], false], 5, 2],
          'line-opacity': ['case', ['boolean', ['feature-state', 'highlighted'], false], 0.95, 0.35],
          'line-dasharray': [
            'case', ['boolean', ['feature-state', 'highlighted'], false],
            ['literal', [1, 0]], ['literal', [2.5, 1.5]],
          ],
        },
      });
      setTimeout(() => { map.resize(); setMapReady(true); }, 150);
    });

    return () => { mapRef.current?.remove(); mapRef.current = null; setMapReady(false); };
  }, []);

  // Draw / update jobs whenever the list changes or map becomes ready
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const currentIds = new Set(jobs.map(j => j.id));
    markersRef.current.forEach(({ pMark, dMark }, id) => {
      if (!currentIds.has(id)) {
        pMark.remove(); dMark.remove();
        markersRef.current.delete(id);
        pinnedRef.current.delete(id);
      }
    });

    const allBounds: [number, number][] = [];
    const features: Feature[] = [];

    jobs.forEach((job, idx) => {
      const color = JOB_COLOURS[idx % JOB_COLOURS.length];
      if (!job.pickupLat || !job.pickupLng || !job.deliveryLat || !job.deliveryLng) return;

      allBounds.push([job.pickupLng, job.pickupLat], [job.deliveryLng, job.deliveryLat]);
      features.push({
        type: 'Feature', id: job.id, properties: { color },
        geometry: {
          type: 'LineString',
          coordinates: [[job.pickupLng, job.pickupLat], [job.deliveryLng, job.deliveryLat]],
        },
      });

      if (markersRef.current.has(job.id)) return; // already drawn

      const onEnter = () => { onHoverRef.current(job.id); hoveredJobIdLive.current = job.id; repaintRoute(job.id); };
      const onLeave = () => { onHoverRef.current(null); hoveredJobIdLive.current = null; repaintRoute(job.id); };

      const pEl = createPinElement('P', color);
      const dEl = createPinElement('D', color);
      pEl.title = `${job.referenceNumber} · ${job.title}`;
      dEl.title = `Delivery · ${job.referenceNumber}`;
      pEl.addEventListener('mouseenter', onEnter);
      pEl.addEventListener('mouseleave', onLeave);
      dEl.addEventListener('mouseenter', onEnter);
      dEl.addEventListener('mouseleave', onLeave);

      pEl.addEventListener('dblclick', e => {
        e.stopPropagation();
        if (pinnedRef.current.has(job.id)) pinnedRef.current.delete(job.id);
        else pinnedRef.current.add(job.id);
        repaintRoute(job.id);
        setPinnedCount(pinnedRef.current.size);
      });

      const pMark = new maplibregl.Marker({ element: pEl })
        .setLngLat([job.pickupLng, job.pickupLat])
        .setPopup(new maplibregl.Popup({ maxWidth: '250px' }).setHTML(
          `<div style="min-width:190px;font-family:system-ui,sans-serif;line-height:1.45">
            <p style="font-weight:700;margin:0 0 3px;color:#111">${job.referenceNumber}</p>
            <p style="font-size:13px;margin:0 0 7px;color:#1f2937">${job.title}</p>
            <p style="font-size:11px;color:#6b7280;margin:0">📍 ${job.pickupAddress}</p>
            <p style="font-size:11px;color:#6b7280;margin:2px 0 7px">🏁 ${job.deliveryAddress}</p>
            <p style="font-size:11px;margin:0 0 7px">${job.distanceMiles.toFixed(1)} mi &nbsp;·&nbsp; ${job.quoteCount === 0 ? '<span style="color:#059669;font-weight:600">No bids yet</span>' : `${job.quoteCount} bid${job.quoteCount !== 1 ? 's' : ''}`}</p>
            <a href="/jobs/${job.id}" style="display:inline-block;font-size:12px;color:#1d4ed8;font-weight:600;text-decoration:none">View Details →</a>
          </div>`
        ))
        .addTo(map);

      const dMark = new maplibregl.Marker({ element: dEl })
        .setLngLat([job.deliveryLng, job.deliveryLat])
        .addTo(map);

      markersRef.current.set(job.id, { pMark, dMark });
    });

    (map.getSource(JOB_ROUTES_SOURCE) as maplibregl.GeoJSONSource)?.setData({ type: 'FeatureCollection', features });

    if (allBounds.length) {
      setTimeout(() => {
        map.resize();
        const bounds = allBounds.reduce((b, c) => b.extend(c), new maplibregl.LngLatBounds(allBounds[0], allBounds[0]));
        map.fitBounds(bounds, { padding: 50 });
      }, 120);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, mapReady]);

  // Highlight from sidebar hover
  useEffect(() => {
    hoveredJobIdLive.current = hoveredJobId;
    markersRef.current.forEach((_m, id) => repaintRoute(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredJobId]);

  // Draw / update live vehicle markers (blue = free, red = on job)
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    const seen = new Set<string>();
    vehicles.forEach(v => {
      seen.add(v.carrierId);
      const onJob = !!v.jobId;
      const stale = new Date(v.recordedAt).getTime() < Date.now() - 3 * 60_000;
      const color = stale ? '#94a3b8' : onJob ? '#dc2626' : '#2563eb';
      const label = v.carrierName.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
      const popupHtml = `<div style="min-width:140px;font-family:system-ui,sans-serif">
        <p style="font-weight:700;margin:0 0 4px">${v.carrierName}</p>
        <p style="font-size:12px;color:${onJob ? '#dc2626' : '#2563eb'};margin:0 0 3px;font-weight:600">${onJob ? '🔴 On Job' : '🔵 Available'}</p>
        <p style="font-size:11px;color:#6b7280;margin:0">Last seen ${new Date(v.recordedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>
      </div>`;

      const existing = vehicleMarkRef.current.get(v.carrierId);
      if (existing) {
        existing.setLngLat([v.lng, v.lat]);
        const el = existing.getElement();
        el.style.background = color;
        el.title = `${v.carrierName} · ${onJob ? 'On job' : 'Available'}`;
        existing.getPopup()?.setHTML(popupHtml);
      } else {
        const el = document.createElement('div');
        el.title = `${v.carrierName} · ${onJob ? 'On job' : 'Available'}`;
        el.style.cssText =
          `background:${color};color:white;border-radius:50%;width:30px;height:30px;` +
          'display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;' +
          'border:2.5px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35);cursor:pointer;';
        el.textContent = label;
        vehicleMarkRef.current.set(v.carrierId,
          new maplibregl.Marker({ element: el })
            .setLngLat([v.lng, v.lat])
            .setPopup(new maplibregl.Popup({ offset: 16 }).setHTML(popupHtml))
            .addTo(map)
        );
      }
    });
    // Remove vehicles no longer in list
    vehicleMarkRef.current.forEach((m, id) => {
      if (!seen.has(id)) { m.remove(); vehicleMarkRef.current.delete(id); }
    });
  }, [vehicles, mapReady]);

  const jobsWithCoords = jobs.filter(j => j.pickupLat && j.deliveryLat);

  return (
    <div className="h-full w-full relative">
      <div ref={containerRef} className="absolute inset-0" />

      {jobsWithCoords.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-50/90 z-[400]">
          <div className="text-center">
            <Navigation2 className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-sm font-medium text-gray-500">No jobs with location data</p>
            <p className="text-xs text-gray-400 mt-1">Try expanding your search radius</p>
          </div>
        </div>
      )}

      {/* Hint overlay */}
      {jobsWithCoords.length > 0 && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[1000] bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow text-xs text-gray-500 pointer-events-none">
          Hover <span className="font-bold text-green-600">P</span> to highlight · Double-click <span className="font-bold text-green-600">P</span> to pin
        </div>
      )}

      {/* Legend */}
      {(jobsWithCoords.length > 0 || vehicles.length > 0) && (
        <div className="absolute bottom-6 right-3 z-[1000] bg-white/95 backdrop-blur-sm rounded-xl shadow-lg p-3 max-h-64 overflow-y-auto" style={{ minWidth: 180 }}>
          {jobsWithCoords.length > 0 && (
            <>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                {jobsWithCoords.length} route{jobsWithCoords.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-1.5 mb-3">
                {jobsWithCoords.map((job, i) => (
                  <div key={job.id} className="flex items-center gap-2 text-xs">
                    <span className="w-3 h-3 rounded-full shrink-0 border border-white shadow-sm" style={{ background: JOB_COLOURS[i % JOB_COLOURS.length] }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-gray-800 font-medium truncate" style={{ maxWidth: 110 }}>{job.title}</div>
                      <div className="text-gray-400 font-mono text-[10px] truncate">{job.referenceNumber}</div>
                    </div>
                    {pinnedRef.current.has(job.id) && (
                      <span className="text-[9px] text-blue-600 font-bold ml-auto shrink-0">📌</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
          {vehicles.length > 0 && (
            <>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5 border-t border-gray-100 pt-2">
                {vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} live
              </p>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-3 h-3 rounded-full bg-blue-600 border border-white shadow-sm shrink-0" />
                  Available
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-3 h-3 rounded-full bg-red-600 border border-white shadow-sm shrink-0" />
                  On job
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

//
//  GEOCODE HELPER
//
async function geocodePostcode(postcode: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.trim())}`);
    const d = await r.json();
    if (d.result?.latitude) return { lat: d.result.latitude, lng: d.result.longitude };
  } catch {}
  return null;
}

//
//  JOB FEED PAGE
//
export function JobFeedPage() {
  const { getToken, account } = useAppAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  //  Search state
  const [coords, setCoords]             = useState({ lat: 51.8787, lng: -0.4200 });
  const [postcode, setPostcode]         = useState('');
  const [postcodeInput, setPostcodeInput] = useState('');
  const [radius, setRadius]             = useState(25);
  const [geocoding, setGeocoding]       = useState(false);
  const [geocodeError, setGeocodeError] = useState<string | null>(null);

  //  Sort / filter state
  const [sortKey,            setSortKey]            = useState<SortKey>('distance-asc');
  const [showFilters,        setShowFilters]        = useState(false);
  const [filterVehicleTypes, setFilterVehicleTypes] = useState<Set<VehicleType>>(new Set());
  const [filterJobTypes,     setFilterJobTypes]     = useState<Set<JobType>>(new Set());
  const [filterFavouritesOnly, setFilterFavouritesOnly] = useState(false);
  const [filterSameDayOnly,  setFilterSameDayOnly]  = useState(false);
  const [filterNoQuotesOnly, setFilterNoQuotesOnly] = useState(false);
  const [filterTwoManOnly,   setFilterTwoManOnly]   = useState(false);
  const [filterTailLiftOnly, setFilterTailLiftOnly] = useState(false);
  const [filterMaxBids,      setFilterMaxBids]      = useState('');
  const [filterMinRoute,     setFilterMinRoute]     = useState('');
  const [filterMaxRoute,     setFilterMaxRoute]     = useState('');
  const [filterScheduledFrom,setFilterScheduledFrom]= useState('');
  const [filterScheduledTo,  setFilterScheduledTo]  = useState('');

  //  Map state
  const [showMap,       setShowMap]       = useState(false);
  const [mapHoveredId,  setMapHoveredId]  = useState<string | null>(null);
  const [liveVehicles,  setLiveVehicles]  = useState<LiveVehicle[]>([]);

  const api  = createApiClient(getToken);
  const prof$ = profileService(api);
  const jobs$ = jobService(api);

  // Auto-populate postcode from job alerts (priority) or profile fallback
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => prof$.getProfile(),
    enabled: !!account,
  });

  const { data: alerts = [] } = useQuery({
    queryKey: ['job-alerts'],
    queryFn: () => jobAlertsService(api).getAll(),
    enabled: !!account,
  });

  const isCarrier  = profile?.role === 'Carrier';
  const isWorkshop = profile?.role === 'Workshop';

  const defaultPostcodeSet = useRef(false);
  useEffect(() => {
    if (defaultPostcodeSet.current || postcode) return;
    const firstAlert = alerts.find(a => a.isEnabled);
    const pc  = firstAlert?.postcode ?? profile?.postcode;
    const rad = firstAlert?.radiusMiles;
    if (pc) {
      defaultPostcodeSet.current = true;
      setPostcodeInput(pc);
      setPostcode(pc);
      if (rad) setRadius(rad);
      geocodePostcode(pc).then(r => { if (r) setCoords(r); });
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          if (defaultPostcodeSet.current) return;
          defaultPostcodeSet.current = true;
          setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        () => {}
      );
    }
  }, [alerts, profile?.postcode]);

  // Dismiss all pending job-alert reminders when the carrier opens the feed
  useEffect(() => {
    if (account) jobs$.attendAlerts();
  }, []);

  //  Data fetch
  const { data: rawJobs = [], isLoading, refetch } = useQuery({
    queryKey: ['jobs', 'available', coords, radius],
    queryFn: () => jobs$.getAvailableJobs(coords.lat, coords.lng, radius),
  });

  useJobHub({ getToken, enabled: !!account, onNewJobAvailable: () => refetch(), onJobAlertMatch: () => refetch() });

  // Live vehicle tracking — only meaningful for carriers with fleet members
  const carrierId = profile?.userId ?? '';
  useEffect(() => {
    if (!carrierId || !showMap) return;
    createApiClient(getToken)
      .get<{ carrierId: string; carrierName: string; jobId?: string | null; latitude: number; longitude: number; recordedAt: string }[]>(
        '/api/carrier/fleet/live-locations'
      )
      .then(r => setLiveVehicles(r.data.map(d => ({
        carrierId: d.carrierId, carrierName: d.carrierName, jobId: d.jobId,
        lat: d.latitude, lng: d.longitude, recordedAt: d.recordedAt,
      }))))
      .catch(() => {});
  }, [carrierId, showMap]);

  useFleetHub({
    getToken,
    carrierId,
    enabled: !!carrierId && showMap,
    onCarrierLocationUpdated: useCallback((u: FleetLocationUpdate) => {
      setLiveVehicles(prev => {
        const existing = prev.find(v => v.carrierId === u.carrierId);
        const updated: LiveVehicle = {
          carrierId: u.carrierId,
          carrierName: existing?.carrierName ?? 'Driver',
          jobId: u.jobId,
          lat: u.lat, lng: u.lng,
          recordedAt: u.recordedAt,
        };
        return [...prev.filter(v => v.carrierId !== u.carrierId), updated];
      });
    }, []),
  });

  const { isRefreshing, lastRefreshed, refresh } = useRefresh(refetch);

  //  Favourite toggle
  const favMutation = useMutation({
    mutationFn: ({ jobId, currently }: { jobId: string; currently: boolean }) =>
      currently
        ? favouriteJobsService(api).remove(jobId)
        : favouriteJobsService(api).add(jobId),
    onMutate: async ({ jobId, currently }) => {
      await queryClient.cancelQueries({ queryKey: ['jobs', 'available', coords, radius] });
      const prev = queryClient.getQueryData<JobSummaryDto[]>(['jobs', 'available', coords, radius]);
      queryClient.setQueryData<JobSummaryDto[]>(
        ['jobs', 'available', coords, radius],
        old => old?.map(j => j.id === jobId ? { ...j, isFavourited: !currently } : j) ?? []
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['jobs', 'available', coords, radius], ctx.prev);
    },
  });


  //  Postcode search
  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    const pc = postcodeInput.trim();
    if (!pc) return;
    setGeocodeError(null);
    setGeocoding(true);
    const result = await geocodePostcode(pc);
    setGeocoding(false);
    if (result) { setCoords(result); setPostcode(pc); }
    else setGeocodeError('Postcode not found — please check and try again.');
  };

  //  Filter + sort (client-side)
  const displayedJobs = useMemo(() => {
    const filtered = rawJobs.filter(job => {
      // Role-based pre-filter: carriers only see delivery jobs; workshops only see repair jobs
      if (isCarrier  && job.jobType === JobType.VehicleRepair) return false;
      if (isWorkshop && job.jobType !== JobType.VehicleRepair) return false;
      if (filterFavouritesOnly && !job.isFavourited) return false;
      if (filterSameDayOnly && !job.isSameDay) return false;
      if (filterNoQuotesOnly && job.quoteCount > 0) return false;
      if (filterTwoManOnly  && !job.requiresTwoMan)  return false;
      if (filterTailLiftOnly && !job.requiresTailLift) return false;
      if (filterVehicleTypes.size > 0 && job.requiredVehicleType != null &&
          !filterVehicleTypes.has(job.requiredVehicleType)) return false;
      if (filterJobTypes.size > 0 && !filterJobTypes.has(job.jobType)) return false;
      if (filterMaxBids !== '' && job.quoteCount > parseInt(filterMaxBids)) return false;
      if (filterMinRoute !== '' && job.distanceMiles < parseFloat(filterMinRoute)) return false;
      if (filterMaxRoute !== '' && job.distanceMiles > parseFloat(filterMaxRoute)) return false;
      if (filterScheduledFrom !== '') {
        if (new Date(job.scheduledAt) < new Date(filterScheduledFrom)) return false;
      }
      if (filterScheduledTo !== '') {
        if (new Date(job.scheduledAt) > new Date(filterScheduledTo)) return false;
      }
      return true;
    });
    return sortJobs(filtered, sortKey);
  }, [
    rawJobs, sortKey, isCarrier, isWorkshop,
    filterFavouritesOnly, filterSameDayOnly, filterNoQuotesOnly, filterTwoManOnly, filterTailLiftOnly,
    filterVehicleTypes, filterJobTypes,
    filterMaxBids, filterMinRoute, filterMaxRoute,
    filterScheduledFrom, filterScheduledTo,
  ]);

  //  Active filter count (badge)
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterFavouritesOnly)    n++;
    if (filterSameDayOnly)       n++;
    if (filterNoQuotesOnly)      n++;
    if (filterTwoManOnly)        n++;
    if (filterTailLiftOnly)      n++;
    if (filterVehicleTypes.size) n++;
    if (filterJobTypes.size)     n++;
    if (filterMaxBids !== '')    n++;
    if (filterMinRoute !== '' || filterMaxRoute !== '') n++;
    if (filterScheduledFrom !== '' || filterScheduledTo !== '') n++;
    return n;
  }, [filterFavouritesOnly, filterSameDayOnly, filterNoQuotesOnly, filterTwoManOnly, filterTailLiftOnly,
      filterVehicleTypes, filterJobTypes,
      filterMaxBids, filterMinRoute, filterMaxRoute,
      filterScheduledFrom, filterScheduledTo]);

  const clearAllFilters = () => {
    setFilterFavouritesOnly(false);
    setFilterVehicleTypes(new Set());
    setFilterJobTypes(new Set());
    setFilterSameDayOnly(false);
    setFilterNoQuotesOnly(false);
    setFilterTwoManOnly(false);
    setFilterTailLiftOnly(false);
    setFilterMaxBids('');
    setFilterMinRoute('');
    setFilterMaxRoute('');
    setFilterScheduledFrom('');
    setFilterScheduledTo('');
  };

  const toggleSet = <T,>(set: Set<T>, val: T): Set<T> => {
    const next = new Set(set);
    next.has(val) ? next.delete(val) : next.add(val);
    return next;
  };

  if (isLoading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-pulse text-blue-700 font-medium">Loading jobs near you...</div>
    </div>
  );

  return (
    <div className="h-[calc(100vh-57px)] bg-gray-50 flex flex-col overflow-hidden">
      {/* ── Single-row toolbar: white + green border ── */}
      <div className="bg-white border-b-2 border-[#0a5c3b] px-4 py-2.5 shrink-0">
        <div className="flex items-center gap-2 flex-nowrap min-w-0">

          {/* Title + count */}
          <div className="shrink-0 mr-1 hidden sm:block">
            <p className="text-sm font-bold text-[#0a5c3b] leading-none">Available Jobs</p>
            <p className="text-[10px] text-gray-400 mt-0.5 leading-none whitespace-nowrap">
              {displayedJobs.length} of {rawJobs.length} within {radius} mi
              {postcode && ` · ${postcode.toUpperCase()}`}
            </p>
          </div>

          {/* Postcode + radius + search */}
          <form onSubmit={handleSearch} className="flex items-center gap-1.5 flex-1 min-w-0">
            <div className="relative flex-1 min-w-0">
              <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#0a5c3b] pointer-events-none" />
              <input
                value={postcodeInput}
                onChange={e => { setPostcodeInput(e.target.value); setGeocodeError(null); }}
                placeholder="ENTER YOUR POSTCODE"
                className="w-full text-xs rounded-lg pl-8 pr-2 py-2 uppercase focus:outline-none focus:ring-2
                           bg-white text-gray-900 placeholder-gray-400 border-2 border-[#0a5c3b] focus:ring-[#0a5c3b]/30"
                maxLength={8}
              />
            </div>
            <select
              value={radius}
              onChange={e => setRadius(Number(e.target.value))}
              className="text-xs rounded-lg px-2 py-2 shrink-0 focus:outline-none focus:ring-2
                         bg-white text-gray-900 border-2 border-[#0a5c3b] focus:ring-[#0a5c3b]/30"
            >
              {[5, 10, 15, 25, 50, 75, 100, 150, 200].map(r => (
                <option key={r} value={r}>{r} mi</option>
              ))}
            </select>
            <button
              type="submit"
              disabled={geocoding || !postcodeInput.trim()}
              className="font-semibold text-xs px-4 py-2 rounded-lg disabled:opacity-50 transition-colors shrink-0
                         bg-[#0a5c3b] text-white hover:bg-[#084d31]"
            >
              {geocoding ? '…' : 'Search'}
            </button>
          </form>

          {/* Sort */}
          <div className="relative shrink-0 hidden md:block" style={{ minWidth: 170 }}>
            <ArrowUpDown className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#0a5c3b] pointer-events-none" />
            <select
              value={sortKey}
              onChange={e => setSortKey(e.target.value as SortKey)}
              className="w-full text-xs rounded-lg pl-8 pr-3 py-2 focus:outline-none focus:ring-2
                         bg-white text-gray-900 border-2 border-[#0a5c3b] focus:ring-[#0a5c3b]/30"
            >
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Filters */}
          <button
            onClick={() => setShowFilters(v => !v)}
            className={`relative flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border transition-colors font-medium shrink-0 ${
              showFilters
                ? 'bg-[#0a5c3b] text-white border-[#0a5c3b]'
                : 'bg-white text-[#0a5c3b] border-[#0a5c3b] hover:bg-green-50'
            }`}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Filters</span>
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 bg-amber-400 text-gray-900 text-[10px]
                               font-bold w-4 h-4 rounded-full flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Map toggle */}
          {!isWorkshop && (
            <button
              onClick={() => setShowMap(v => !v)}
              title={showMap ? 'Switch to list view' : 'Switch to map view'}
              className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border font-medium transition-colors shrink-0 ${
                showMap
                  ? 'bg-[#0a5c3b] text-white border-[#0a5c3b] hover:bg-[#084d31]'
                  : 'bg-white text-[#0a5c3b] border-[#0a5c3b] hover:bg-green-50'
              }`}
            >
              <MapIcon className="w-4 h-4" />
              <span className="hidden sm:inline">{showMap ? 'List' : 'Map'}</span>
            </button>
          )}

          {/* Refresh */}
          <RefreshButton
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            lastRefreshed={lastRefreshed}
            className="text-gray-400 hover:text-[#0a5c3b] hover:bg-green-50 shrink-0"
          />
        </div>

        {geocodeError && <p className="mt-1 text-xs text-red-500">{geocodeError}</p>}
      </div>

      {/* ── Filter panel ── */}
      {showFilters && (
        <div className="bg-white border-b-2 border-[#0a5c3b] px-4 py-4 space-y-4 shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4 text-[#0a5c3b]" />
              Filter Jobs
            </h2>
            {activeFilterCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium"
              >
                <RotateCcw className="w-3 h-3" /> Clear all ({activeFilterCount})
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <ToggleChip label="❤️ Favourites only"  active={filterFavouritesOnly} onClick={() => setFilterFavouritesOnly(v => !v)} />
            <ToggleChip label="⚡ Same-day only"    active={filterSameDayOnly}    onClick={() => setFilterSameDayOnly(v => !v)} />
            <ToggleChip label="🏆 No bids yet"      active={filterNoQuotesOnly}   onClick={() => setFilterNoQuotesOnly(v => !v)} />
            <ToggleChip label="👥 Two-man crew"     active={filterTwoManOnly}     onClick={() => setFilterTwoManOnly(v => !v)} />
            <ToggleChip label="🔧 Tail lift needed" active={filterTailLiftOnly}   onClick={() => setFilterTailLiftOnly(v => !v)} />
          </div>

          <FilterSection title="Vehicle Type Required">
            <div className="flex flex-wrap gap-2">
              {(Object.values(VehicleType).filter(v => typeof v === 'number') as VehicleType[]).map(vt => (
                <ToggleChip key={vt} label={VEHICLE_LABELS[vt]}
                  active={filterVehicleTypes.has(vt)}
                  onClick={() => setFilterVehicleTypes(s => toggleSet(s, vt))} />
              ))}
            </div>
          </FilterSection>

          <FilterSection title="Job Type">
            <div className="flex flex-wrap gap-2">
              {(Object.values(JobType).filter(v => typeof v === 'number') as JobType[])
                .filter(jt => {
                  if (isCarrier)  return jt !== JobType.VehicleRepair;
                  if (isWorkshop) return jt === JobType.VehicleRepair;
                  return true;
                })
                .map(jt => (
                  <ToggleChip key={jt} label={JOB_TYPE_LABELS[jt]}
                    active={filterJobTypes.has(jt)}
                    onClick={() => setFilterJobTypes(s => toggleSet(s, jt))} />
                ))}
            </div>
          </FilterSection>

          <div className="grid grid-cols-2 gap-4">
            <FilterSection title="Max Bids (competition)">
              <input
                type="number" min={0} placeholder="e.g. 3"
                value={filterMaxBids}
                onChange={e => setFilterMaxBids(e.target.value)}
                className={inputCls}
              />
              <p className="text-xs text-gray-400 mt-1">Show jobs with &lt;= N bids</p>
            </FilterSection>

            <FilterSection title="Route Length (miles)">
              <div className="flex items-center gap-1">
                <input type="number" min={0} placeholder="Min" value={filterMinRoute}
                  onChange={e => setFilterMinRoute(e.target.value)} className={inputCls + ' w-1/2'} />
                <span className="text-gray-400 text-xs">–</span>
                <input type="number" min={0} placeholder="Max" value={filterMaxRoute}
                  onChange={e => setFilterMaxRoute(e.target.value)} className={inputCls + ' w-1/2'} />
              </div>
            </FilterSection>

            <FilterSection title="Scheduled Window">
              <div className="space-y-1">
                <input type="datetime-local" value={filterScheduledFrom}
                  onChange={e => setFilterScheduledFrom(e.target.value)} className={inputCls} title="Scheduled from" />
                <input type="datetime-local" value={filterScheduledTo}
                  onChange={e => setFilterScheduledTo(e.target.value)} className={inputCls} title="Scheduled to" />
              </div>
            </FilterSection>
          </div>

          {activeFilterCount > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1 border-t border-gray-100">
              {filterFavouritesOnly && <ActiveTag label="Favourites" onRemove={() => setFilterFavouritesOnly(false)} />}
              {filterSameDayOnly && <ActiveTag label="Same-day" onRemove={() => setFilterSameDayOnly(false)} />}
              {filterNoQuotesOnly && <ActiveTag label="No bids" onRemove={() => setFilterNoQuotesOnly(false)} />}
              {filterTwoManOnly && <ActiveTag label="Two-man" onRemove={() => setFilterTwoManOnly(false)} />}
              {filterTailLiftOnly && <ActiveTag label="Tail lift" onRemove={() => setFilterTailLiftOnly(false)} />}
              {[...filterVehicleTypes].map(vt => (
                <ActiveTag key={vt} label={VEHICLE_LABELS[vt]} onRemove={() => setFilterVehicleTypes(s => toggleSet(s, vt))} />
              ))}
              {[...filterJobTypes].map(jt => (
                <ActiveTag key={jt} label={JOB_TYPE_LABELS[jt]} onRemove={() => setFilterJobTypes(s => toggleSet(s, jt))} />
              ))}
              {filterMaxBids !== '' && (
                <ActiveTag label={`≤${filterMaxBids} bids`} onRemove={() => setFilterMaxBids('')} />
              )}
              {(filterMinRoute !== '' || filterMaxRoute !== '') && (
                <ActiveTag label={`Route ${filterMinRoute || '0'}–${filterMaxRoute || '∞'} mi`}
                  onRemove={() => { setFilterMinRoute(''); setFilterMaxRoute(''); }} />
              )}
              {(filterScheduledFrom !== '' || filterScheduledTo !== '') && (
                <ActiveTag label="Schedule window"
                  onRemove={() => { setFilterScheduledFrom(''); setFilterScheduledTo(''); }} />
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Main content ── */}
      {showMap ? (
        <div className="flex-1 min-h-0 relative">
          <JobsFullMap
            jobs={displayedJobs}
            vehicles={liveVehicles}
            hoveredJobId={mapHoveredId}
            onJobHover={setMapHoveredId}
          />
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto bg-white">

          {/* Sticky table header */}
          {displayedJobs.length > 0 && (
            <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-3 px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-wide">
                <span style={{ width: 188 }} className="shrink-0">Type / Status</span>
                <span className="flex-1 min-w-0">Job</span>
                <span style={{ width: 260 }} className="shrink-0 hidden lg:block">Route</span>
                <span style={{ width: 130 }} className="shrink-0 hidden xl:block">Vehicle</span>
                <span style={{ width: 120 }} className="shrink-0 hidden md:block">Scheduled</span>
                <span style={{ width: 64 }}  className="shrink-0 text-right">Bids</span>
                <span style={{ width: 56 }}  className="shrink-0" />
              </div>
            </div>
          )}

          {/* Empty states */}
          {displayedJobs.length === 0 && rawJobs.length > 0 && (
            <div className="text-center py-16 text-gray-500">
              <SlidersHorizontal className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No jobs match your filters</p>
              <button onClick={clearAllFilters} className="mt-3 text-sm text-[#0a5c3b] hover:underline font-medium">
                Clear filters
              </button>
            </div>
          )}

          {rawJobs.length === 0 && (
            <div className="text-center py-20 text-gray-500">
              <Truck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No jobs available right now</p>
              <p className="text-sm mt-1">Check back soon or expand your search radius</p>
            </div>
          )}

          {/* Job rows */}
          <div className="divide-y divide-gray-100">
            {displayedJobs.map(job => (
              <div
                key={job.id}
                onClick={() => navigate(`/jobs/${job.id}`)}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50/50 transition-colors cursor-pointer group"
              >
                {/* Tags */}
                <div style={{ width: 188 }} className="shrink-0 flex flex-wrap gap-1 items-center">
                  {job.isSameDay && (
                    <span className="inline-flex items-center gap-0.5 bg-orange-100 text-orange-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      <Zap className="w-2.5 h-2.5" /> Same Day
                    </span>
                  )}
                  <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    {JOB_TYPE_LABELS[job.jobType]}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${STATUS_COLOURS[job.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {JobStatus[job.status]}
                  </span>
                  {job.quoteCount === 0 && (
                    <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">
                      First to bid
                    </span>
                  )}
                  {job.customerEditedAt && (
                    <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                      Edited
                    </span>
                  )}
                  {(job.jobType === JobType.MultiDropDelivery || job.jobType === JobType.MultiPickupDropDelivery) && (job.dropCount ?? 0) > 0 && (
                    <span className="text-[10px] bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full whitespace-nowrap font-medium">
                      {job.dropCount} drop{job.dropCount !== 1 ? 's' : ''}
                    </span>
                  )}
                  {job.jobType === JobType.MultiPickupDropDelivery && (job.pickupCount ?? 0) > 0 && (
                    <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full whitespace-nowrap font-medium">
                      {job.pickupCount} pickup{job.pickupCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>

                {/* Title + ref */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate group-hover:text-[#0a5c3b] transition-colors">
                    {job.title}
                  </p>
                  <p className="text-[11px] text-gray-400 font-mono mt-0.5">{job.referenceNumber}</p>
                </div>

                {/* Route */}
                <div style={{ width: 260 }} className="shrink-0 hidden lg:flex flex-col gap-0.5 min-w-0">
                  {job.jobType === JobType.VehicleRepair ? (
                    <div className="flex items-center gap-1 text-xs text-gray-500 min-w-0">
                      <MapPin className="w-3 h-3 text-blue-500 shrink-0" />
                      <span className="truncate">{job.pickupAddress}</span>
                      {job.isPickupAddressHidden && <Lock className="w-2.5 h-2.5 text-amber-500 shrink-0" />}
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1 text-xs text-gray-500 min-w-0">
                        <MapPin className="w-3 h-3 text-green-500 shrink-0" />
                        <span className="truncate">{job.pickupAddress}</span>
                        {job.isPickupAddressHidden && <Lock className="w-2.5 h-2.5 text-amber-500 shrink-0" />}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-500 min-w-0">
                        <MapPin className="w-3 h-3 text-red-500 shrink-0" />
                        <span className="truncate">{job.deliveryAddress}</span>
                        {job.isDeliveryAddressHidden && <Lock className="w-2.5 h-2.5 text-amber-500 shrink-0" />}
                      </div>
                    </>
                  )}
                </div>

                {/* Vehicle + distance */}
                <div style={{ width: 130 }} className="shrink-0 hidden xl:block min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate">
                    {VEHICLE_ICONS[job.requiredVehicleType ?? VehicleType.SmallVan]}{' '}
                    {VEHICLE_LABELS[job.requiredVehicleType ?? VehicleType.SmallVan]}
                    {job.requiresTwoMan && <span title="Two-man crew" className="ml-1">👥</span>}
                    {job.requiresTailLift && <span title="Tail lift" className="ml-1 text-amber-600">🔧</span>}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {job.distanceMiles.toFixed(1)} mi
                    {job.distanceFromCarrierMiles != null && ` · ${job.distanceFromCarrierMiles.toFixed(1)} mi away`}
                  </p>
                  {/* Charging zones on the route. Worth knowing before you quote. */}
                  {routeZoneLabels(job.routeZoneCodes).length > 0 && (
                    <p className="text-[11px] text-amber-700 mt-0.5 truncate" data-testid="job-route-zones">
                      🛣️ {routeZoneLabels(job.routeZoneCodes).join(' · ')}
                    </p>
                  )}
                </div>

                {/* Scheduled */}
                <div style={{ width: 120 }} className="shrink-0 hidden md:flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                  <Clock className="w-3 h-3 shrink-0" />
                  {new Date(job.scheduledAt).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </div>

                {/* Bids */}
                <div style={{ width: 64 }} className="shrink-0 text-right whitespace-nowrap">
                  {job.quoteCount > 0
                    ? <span className="text-xs text-blue-600 font-medium">{job.quoteCount} bid{job.quoteCount !== 1 ? 's' : ''}</span>
                    : <span className="text-xs text-emerald-600 font-medium">No bids</span>
                  }
                </div>

                {/* Actions */}
                <div style={{ width: 56 }} className="shrink-0 flex items-center justify-end gap-0.5">
                  <button
                    onClick={e => { e.stopPropagation(); favMutation.mutate({ jobId: job.id, currently: !!job.isFavourited }); }}
                    className={`p-1.5 rounded-lg transition-colors ${
                      job.isFavourited ? 'text-amber-500 bg-amber-50' : 'text-gray-300 hover:text-amber-400 hover:bg-amber-50'
                    }`}
                    title={job.isFavourited ? 'Remove favourite' : 'Add to favourites'}
                  >
                    <Heart className={`w-3.5 h-3.5 ${job.isFavourited ? 'fill-current' : ''}`} />
                  </button>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#0a5c3b] transition-colors" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
//  SMALL REUSABLE COMPONENTS
// 
const inputCls = 'w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
      {children}
    </div>
  );
}

function ToggleChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${
        active
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
      }`}
    >
      {label}
    </button>
  );
}

function ActiveTag({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs
                     px-2 py-1 rounded-full border border-blue-200 font-medium">
      {label}
      <button onClick={onRemove} className="hover:text-blue-900 ml-0.5">
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}

// 
//  QUOTE EVENT LOG  (collapsible, default closed)
// 
function QuoteEventLog({
  quote,
  carrierAcceptedAt,
}: {
  quote: QuoteSummaryDto;
  carrierAcceptedAt?: string | null;
}) {
  const [open, setOpen] = useState(false);

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });

  const events: { label: string; time: string | null; colour: string }[] = [
    { label: 'Quote submitted', time: quote.submittedAt, colour: 'bg-blue-400' },
    ...(quote.status === QuoteStatus.Accepted
      ? [{ label: 'Customer accepted quote', time: quote.acceptedAt ?? null, colour: 'bg-green-500' }]
      : []),
    ...(carrierAcceptedAt
      ? [{ label: 'Carrier accepted job', time: carrierAcceptedAt, colour: 'bg-teal-500' }]
      : []),
  ];

  return (
    <div className="mt-3 border border-gray-100 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100
                   text-xs font-medium text-gray-600 transition-colors"
      >
        <span>Activity ({events.length} event{events.length !== 1 ? 's' : ''})</span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <div className="px-3 py-3 space-y-3 bg-white">
          {events.map((ev, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <div className="flex flex-col items-center shrink-0 pt-0.5">
                <span className={`w-2.5 h-2.5 rounded-full ${ev.colour}`} />
                {i < events.length - 1 && <span className="w-px flex-1 bg-gray-200 mt-1 min-h-[1rem]" />}
              </div>
              <div className="text-xs">
                <p className="font-medium text-gray-800">{ev.label}</p>
                <p className="text-gray-400 mt-0.5">
                  {ev.time ? fmt(ev.time) : 'Time not recorded'}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// 
//  JOB DETAIL PAGE  inline driver view
// 
export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { getToken, account } = useAppAuth();
  const queryClient = useQueryClient();
  const [quoteAmount, setQuoteAmount]   = useState('');
  const [quoteLabour, setQuoteLabour]   = useState('');
  const [quoteParts, setQuoteParts]     = useState('');
  const [quoteMessage, setQuoteMessage] = useState('');
  const [notification, setNotification] = useState<string | null>(null);
  const [editOpen, setEditOpen]         = useState(false);
  const [editFields, setEditFields]     = useState({
    title: '', description: '', goodsDescription: '',
    scheduledAt: '', estimatedValueGbp: '', requiredVehicleType: 0,
  });
  const [paymentResult, setPaymentResult] = useState<'success' | 'cancelled' | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [reviewRating,      setReviewRating]      = useState(0);
  const [reviewComment,     setReviewComment]     = useState('');
  const [reviewSubmitting,  setReviewSubmitting]  = useState(false);
  const [reviewSuccess,     setReviewSuccess]     = useState(false);
  const [reviewError,       setReviewError]       = useState<string | null>(null);

  const api = createApiClient(getToken);

  useEffect(() => {
    const result = searchParams.get('payment');
    if (result === 'success' || result === 'cancelled') {
      setPaymentResult(result);
      setSearchParams(prev => { prev.delete('payment'); prev.delete('party'); return prev; }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const { data: job, isLoading } = useQuery({
    queryKey: ['job', id],
    queryFn: () => jobService(api).getJob(id!),
    enabled: !!id,
  });

  const { data: quotes = [], refetch: refetchQuotes } = useQuery({
    queryKey: ['quotes', id],
    queryFn: () => quoteService(api).getQuotesForJob(id!),
    enabled: !!id,
  });

  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: () => profileService(api).getProfile(),
    enabled: !!account,
  });

  const { data: feeConfig } = useQuery({
    queryKey: ['platform', 'fee-config'],
    queryFn: () => platformFeeConfigService(api).getEffective(),
  });
  const commissionPercent = job?.jobType === JobType.VehicleRepair
    ? feeConfig?.repairJobCommissionPercent
    : feeConfig?.deliveryJobCommissionPercent;

  const isCustomer = profile?.role === 'Customer';
  const isCarrier  = profile?.role === 'Carrier';

  const { data: eligibility } = useQuery<QuoteEligibilityResult>({
    queryKey: ['eligibility'],
    queryFn: () => eligibilityService(api).getMyEligibility(),
    enabled: !!account && isCarrier,
  });

  const { data: unreadPerCarrier = [] } = useQuery({
    queryKey: ['unread-per-carrier', id],
    queryFn: async () => {
      const token = await getToken();
      return messagesApi.getUnreadPerCarrier(id!, token!);
    },
    enabled: !!id && isCustomer,
    refetchInterval: 30_000,
  });
  const isApprovedDriver =
    profile?.registrationStatus === 'Approved' || profile?.registrationStatus === 'Verified';

  useJobHub({
    getToken,
    enabled: !!account,
    jobId: id,
    onQuoteReceived: useCallback((_e: QuoteReceivedEvent) => {
      refetchQuotes();
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      setNotification('New quote received!');
      setTimeout(() => setNotification(null), 4000);
    }, [refetchQuotes, queryClient, id]),
    onContractGenerated: useCallback((_e: ContractGeneratedEvent) => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      setNotification('Contract generated  check your email!');
    }, [queryClient, id]),
  });

  const submitQuoteMutation = useMutation({
    mutationFn: () => {
      const isRepair = job?.jobType === JobType.VehicleRepair;
      return quoteService(api).submitQuote(
        isRepair
          ? { jobId: id!, amount: 0, labourAmount: parseFloat(quoteLabour), partsAmount: parseFloat(quoteParts || '0'), message: quoteMessage || undefined }
          : { jobId: id!, amount: parseFloat(quoteAmount), message: quoteMessage || undefined }
      );
    },
    onSuccess: () => {
      setQuoteAmount(''); setQuoteLabour(''); setQuoteParts(''); setQuoteMessage('');
      refetchQuotes();
      setNotification('Quote submitted successfully!');
      setTimeout(() => setNotification(null), 3000);
    },
  });

  const acceptQuoteMutation = useMutation({
    mutationFn: (quoteId: string) => quoteService(api).acceptQuote(quoteId, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      queryClient.invalidateQueries({ queryKey: ['quotes', id] });
    },
  });

  const rejectQuoteMutation = useMutation({
    mutationFn: (quoteId: string) => quoteService(api).rejectQuote(quoteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes', id] });
      setNotification('Quote rejected.');
      setTimeout(() => setNotification(null), 3000);
    },
  });

  const carrierAcceptMutation = useMutation({
    mutationFn: () => jobService(api).carrierAcceptJob(id!),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['job', id] });
      queryClient.refetchQueries({ queryKey: ['quotes', id] });
      setNotification('You have accepted this job. Customer has been notified.');
      setTimeout(() => setNotification(null), 4000);
    },
    onError: () => {
      setNotification('Failed to accept job. Please try again.');
      setTimeout(() => setNotification(null), 4000);
    },
  });

  const paymentIntentMutation = useMutation({
    mutationFn: () => jobService(api).createCustomerCheckoutUrl(id!),
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: () => {
      setNotification('Failed to create payment link. Please try again.');
      setTimeout(() => setNotification(null), 5000);
    },
  });

  const carrierCommissionMutation = useMutation({
    mutationFn: () => jobService(api).createCarrierCheckoutUrl(id!),
    onSuccess: (url) => {
      window.location.href = url;
    },
    onError: () => {
      setNotification('Failed to create commission payment link. Please try again.');
      setTimeout(() => setNotification(null), 5000);
    },
  });

  const editMutation = useMutation({
    mutationFn: () => jobService(api).editJob(id!, {
      title:               editFields.title,
      description:         editFields.description,
      goodsDescription:    editFields.goodsDescription,
      isSameDay:           job?.isSameDay ?? false,
      scheduledAt:         new Date(editFields.scheduledAt).toISOString(),
      estimatedValueGbp:   editFields.estimatedValueGbp ? parseFloat(editFields.estimatedValueGbp) : null,
      requiredVehicleType: editFields.requiredVehicleType,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', id] });
      setEditOpen(false);
      setNotification('Job updated successfully.');
      setTimeout(() => setNotification(null), 3000);
    },
  });

  // Diagnostic log  helps trace why driver accept / payment buttons appear or not
  useEffect(() => {
    if (!job) return;
    const myAcceptedQuote = job.quotes.find(
      q => q.status === QuoteStatus.Accepted && q.carrierId === (profile?.userId ?? '')
    );
    console.log('[JobDetail] render state', {
      jobId: id,
      jobStatus: job.status,
      isCustomer,
      profileUserId: profile?.userId,
      carrierAcceptedAt: job.carrierAcceptedAt,
      myAcceptedQuote: myAcceptedQuote
        ? { id: myAcceptedQuote.id, carrierId: myAcceptedQuote.carrierId, status: myAcceptedQuote.status }
        : null,
      allAcceptedQuotes: job.quotes
        .filter(q => q.status === QuoteStatus.Accepted)
        .map(q => ({ carrierId: q.carrierId, status: q.status })),
    });
  }, [id, job, isCustomer, profile?.userId]);

  const handleSubmitReview = async (revieweeId: string) => {
    if (!id || reviewRating === 0 || !revieweeId) return;
    setReviewSubmitting(true);
    setReviewError(null);
    try {
      await api.post('/api/reviews', {
        jobId: id,
        revieweeId,
        rating: reviewRating,
        comment: reviewComment.trim() || null,
      });
      setReviewSuccess(true);
    } catch (err: any) {
      setReviewError(err.response?.data?.error ?? 'Failed to submit review.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  if (isLoading || !job) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-pulse text-blue-700">Loading job details...</div>
    </div>
  );

  const isConfirmed   = job.status >= JobStatus.Accepted;
  const isRepairJob   = job.jobType === JobType.VehicleRepair;
  const acceptedQuote = quotes.find(q => q.status === QuoteStatus.Accepted);

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {notification && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white px-4 py-3 rounded-xl
                        shadow-lg text-sm font-medium animate-bounce">
          {notification}
        </div>
      )}

      <div className="bg-blue-700 px-4 py-4 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold">{job.title}</h1>
            <p className="text-blue-200 text-sm">{job.referenceNumber}</p>
          </div>
          <div className="flex items-center gap-2">
            {isCustomer && !isConfirmed && (
              <button
                onClick={() => {
                  setEditFields({
                    title:               job.title,
                    description:         job.description ?? '',
                    goodsDescription:    job.goodsDescription,
                    scheduledAt:         new Date(job.scheduledAt).toISOString().slice(0, 16),
                    estimatedValueGbp:   job.estimatedValueGbp != null ? String(job.estimatedValueGbp) : '',
                    requiredVehicleType: job.requiredVehicleType,
                  });
                  setEditOpen(true);
                }}
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white
                           text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" /> Edit
              </button>
            )}
            <span className={`text-xs font-semibold px-3 py-1 rounded-full
              ${STATUS_COLOURS[job.status] ?? 'bg-blue-600 text-white'}`}>
              {JobStatus[job.status]}
            </span>
          </div>
        </div>
      </div>

      {/*  Edit Job Modal  */}
      {editOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Edit Job</h2>
              <button onClick={() => setEditOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                <input
                  value={editFields.title}
                  onChange={e => setEditFields(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={editFields.description}
                  onChange={e => setEditFields(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Goods Description</label>
                <textarea
                  rows={2}
                  value={editFields.goodsDescription}
                  onChange={e => setEditFields(f => ({ ...f, goodsDescription: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Scheduled At</label>
                <input
                  type="datetime-local"
                  value={editFields.scheduledAt}
                  onChange={e => setEditFields(f => ({ ...f, scheduledAt: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Estimated Value (£)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={editFields.estimatedValueGbp}
                    onChange={e => setEditFields(f => ({ ...f, estimatedValueGbp: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Vehicle Required</label>
                  <select
                    value={editFields.requiredVehicleType}
                    onChange={e => setEditFields(f => ({ ...f, requiredVehicleType: parseInt(e.target.value) }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  >
                    {Object.entries(VEHICLE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
              {editMutation.isError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  Failed to save changes. Please try again.
                </p>
              )}
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button
                onClick={() => setEditOpen(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => editMutation.mutate()}
                disabled={editMutation.isPending || !editFields.title.trim()}
                className="flex-1 bg-blue-700 hover:bg-blue-800 disabled:bg-gray-300 text-white py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {editMutation.isPending ? 'Saving' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="w-full mt-4 space-y-4">
        {/* Confirmed banner for non-customers */}
        {isConfirmed && !isCustomer && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-600 flex items-start gap-3">
            <span className="text-lg">"'</span>
            <div>
              <p className="font-semibold text-gray-700">This job is closed for new bids</p>
              <p>A quote has already been accepted.</p>
            </div>
          </div>
        )}

        {/* Job Info */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="font-semibold text-gray-900 mb-3">Job Details</h2>
          <div className="space-y-2 text-sm">
            <InfoRow label="Type"         value={JOB_TYPE_LABELS[job.jobType]} />
            <InfoRow label="Vehicle"      value={VEHICLE_LABELS[job.requiredVehicleType]} />
            <InfoRow label="Route"        value={`${job.distanceMiles.toFixed(1)} miles`} />
            <InfoRow
              label="Scheduled"
              value={new Date(job.scheduledAt).toLocaleString('en-GB', {
                weekday: 'short', day: 'numeric', month: 'short',
                hour: '2-digit', minute: '2-digit',
              })}
            />
            {job.isSameDay && (
              <div className="inline-flex items-center gap-1 bg-orange-50 text-orange-700
                              text-xs font-semibold px-2 py-1 rounded-lg mt-1">
                <Zap className="w-3.5 h-3.5" /> Same-day priority
              </div>
            )}
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 text-sm">
            {job.jobType === JobType.VehicleRepair ? (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Location</p>
                <p className="text-gray-700 flex items-center gap-1">
                  {job.pickupAddress}
                  {job.isPickupAddressHidden && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                </p>
              </div>
            ) : (
              <>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Pickup</p>
                  <p className="text-gray-700 flex items-center gap-1">
                    {job.pickupAddress}
                    {job.isPickupAddressHidden && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-0.5">Delivery</p>
                  <p className="text-gray-700 flex items-center gap-1">
                    {job.deliveryAddress}
                    {job.isDeliveryAddressHidden && <Lock className="w-3 h-3 text-amber-500 shrink-0" />}
                  </p>
                </div>
              </>
            )}
            <div><p className="text-xs text-gray-400 mb-0.5">Goods</p><p className="text-gray-700">{job.goodsDescription}</p></div>
          </div>
        </div>

        {/* Route map */}
        {job.isRelayJob && job.relaySegments?.length > 0 ? (
          <RelayMap
            segments={job.relaySegments}
            pickupLat={job.pickupLat} pickupLng={job.pickupLng}
            deliveryLat={job.deliveryLat} deliveryLng={job.deliveryLng}
          />
        ) : (
          <RouteMap
            pickupLat={job.pickupLat} pickupLng={job.pickupLng} pickupAddress={job.pickupAddress}
            deliveryLat={job.deliveryLat} deliveryLng={job.deliveryLng} deliveryAddress={job.deliveryAddress}
            live={job.jobType === JobType.SameDayDelivery}
          />
        )}

        {/* Goods images */}
        {job.goodsImageUrls && job.goodsImageUrls.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-3">Goods Images</h2>
            <div className="grid grid-cols-2 gap-2">
              {job.goodsImageUrls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noreferrer">
                  <img
                    src={url}
                    alt={`Goods image ${i + 1}`}
                    className="w-full h-36 object-cover rounded-lg border border-gray-100"
                  />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Submit Quote */}
        {!isCustomer && !isConfirmed && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-3">Submit Your Quote</h2>
            {!isApprovedDriver ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                <p className="font-semibold mb-1">Account approval required</p>
                <p>Your account is pending admin review. You will be notified once approved.</p>
                <a href="/profile" className="inline-block mt-2 text-blue-700 font-medium hover:underline">View profile '</a>
              </div>
            ) : eligibility && !eligibility.isEligible ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                <p className="font-semibold mb-1">Membership required</p>
                <p>Complete at least one membership route and ensure all your vehicles have verified H&amp;R Insurance and MOT certificates.</p>
                <a href="/profile" className="text-blue-700 font-medium hover:underline mt-2 inline-block">View membership status '</a>
              </div>
            ) : job?.jobType === JobType.VehicleRepair ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Labour inc. VAT (£)</label>
                    <input type="number" value={quoteLabour}
                      onChange={e => setQuoteLabour(e.target.value)}
                      placeholder="Labour amount"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Parts inc. VAT (£)</label>
                    <input type="number" value={quoteParts}
                      onChange={e => setQuoteParts(e.target.value)}
                      placeholder="Parts amount"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                                 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </div>
                {quoteLabour && parseFloat(quoteLabour) > 0 && commissionPercent != null && (
                  <p className="text-xs text-gray-400">
                    Total: £{(parseFloat(quoteLabour) + parseFloat(quoteParts || '0')).toFixed(2)} —
                    You receive: £{((parseFloat(quoteLabour) * (1 - commissionPercent / 100)) + parseFloat(quoteParts || '0')).toFixed(2)} ({commissionPercent}% commission on labour only)
                  </p>
                )}
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Message (optional)</label>
                  <textarea value={quoteMessage} onChange={e => setQuoteMessage(e.target.value)}
                    placeholder="Tell the customer why you're the right driver for this job…"
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                               focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <button onClick={() => submitQuoteMutation.mutate()}
                  disabled={!quoteLabour || submitQuoteMutation.isPending}
                  className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-gray-300
                             text-white py-2.5 rounded-lg font-medium text-sm transition-colors">
                  {submitQuoteMutation.isPending ? 'Submitting...' : 'Submit Quote'}
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Your quote amount (£)</label>
                  <input type="number" value={quoteAmount}
                    onChange={e => setQuoteAmount(e.target.value)}
                    placeholder="Enter your quote amount"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                               focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  {quoteAmount && parseFloat(quoteAmount) > 0 && commissionPercent != null && (
                    <p className="text-xs text-gray-400 mt-1">
                      You receive: £{(parseFloat(quoteAmount) * (1 - commissionPercent / 100)).toFixed(2)} (after {commissionPercent}% platform fee)
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Message (optional)</label>
                  <textarea value={quoteMessage} onChange={e => setQuoteMessage(e.target.value)}
                    placeholder="Tell the customer why you're the right driver for this job..."
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                               focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                </div>
                <button onClick={() => submitQuoteMutation.mutate()}
                  disabled={!quoteAmount || submitQuoteMutation.isPending}
                  className="w-full bg-blue-700 hover:bg-blue-800 disabled:bg-gray-300
                             text-white py-2.5 rounded-lg font-medium text-sm transition-colors">
                  {submitQuoteMutation.isPending ? 'Submitting...' : 'Submit Quote'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Quotes list */}
        {quotes.length > 0 && (
          <QuotesSection
            quotes={quotes}
            jobStatus={job.status}
            jobId={id!}
            isCustomer={isCustomer}
            customerId={job.customerId}
            currentUserId={account?.userId ?? ''}
            profileUserId={profile?.userId ?? ''}
            currentUserName={profile?.firstName ? `${profile.firstName} ${profile.lastName ?? ''}`.trim() : 'Me'}
            unreadPerCarrier={unreadPerCarrier}
            carrierAcceptedAt={job.carrierAcceptedAt}
            getToken={getToken}
            onAccept={(quoteId) => acceptQuoteMutation.mutate(quoteId)}
            onReject={(quoteId) => rejectQuoteMutation.mutate(quoteId)}
            actionPending={acceptQuoteMutation.isPending || rejectQuoteMutation.isPending}
            commissionPercent={commissionPercent}
          />
        )}

        {/* Relay segments */}
        {job.isRelayJob && job.relaySegments.length > 0 && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-3">Relay Segments</h2>
            <div className="space-y-2">
              {job.relaySegments.map(seg => (
                <div key={seg.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-blue-700">Segment {seg.segmentOrder}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      seg.status === 4 ? 'bg-green-100 text-green-700' :
                      seg.status === 1 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                    }`}>{seg.assignedCarrierName ?? 'Unassigned'}</span>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{seg.pickupAddress} ' {seg.deliveryAddress}</p>
                  <p className="text-xs text-gray-500">{seg.distanceMiles.toFixed(1)} miles· £{seg.totalRelayFee.toFixed(2)} relay fee</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contract */}
        {job.contract && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <h2 className="font-semibold text-indigo-900 mb-2">Contract Generated</h2>
            <p className="text-sm text-indigo-700">Ref: {job.contract.contractReference}</p>
            {isCustomer && (
              <p className="text-sm text-indigo-700">Total: £{job.contract.agreedPrice.toFixed(2)}</p>
            )}
            <a href={job.contract.contractPdfUrl} target="_blank" rel="noreferrer"
              className="mt-3 inline-block text-sm text-indigo-700 underline font-medium">
              Download Contract PDF '
            </a>
          </div>
        )}

        {/* Step 4b / 5: Driver accepts the job  shown at Accepted (4) AND ContractGenerated (5).
             Uses profile.userId (internal DB GUID) to match quote.carrierId correctly in production.
             Hidden once carrierAcceptedAt is set — for Cash, status stays 4 after carrier accepts. */}
        {(job.status === JobStatus.Accepted || job.status === JobStatus.ContractGenerated) &&
          !job.carrierAcceptedAt &&
          !isCustomer &&
          quotes.some(q => q.status === QuoteStatus.Accepted && q.carrierId === (profile?.userId ?? '')) && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
            <h2 className="font-semibold text-teal-900 mb-1">
              {job.status === JobStatus.Accepted ? 'Customer Has Accepted Your Quote' : 'Accept This Job'}
            </h2>
            <p className="text-sm text-teal-700 mb-3">
              {job.status === JobStatus.Accepted
                ? 'The customer has chosen your quote. Please confirm you accept this job to proceed.'
                : 'The contract has been generated. Please review it and confirm you accept the job.'}
            </p>
            <button
              onClick={() => {
                  console.log('[JobDetail] carrier accept clicked', { jobId: id, jobStatus: job.status, carrierId: profile?.userId });
                  carrierAcceptMutation.mutate();
                }}
                disabled={carrierAcceptMutation.isPending}
              className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white
                         py-2.5 rounded-lg font-medium text-sm transition-colors"
            >
                {carrierAcceptMutation.isPending ? 'Confirming...' : 'Accept Job'}
            </button>
          </div>
        )}

        {/* Carrier confirmed — Cash flow only: status stays Accepted(4), carrierAcceptedAt is set, awaiting customer payment */}
        {!isCustomer && job.status === JobStatus.Accepted && !!job.carrierAcceptedAt &&
          quotes.some(q => q.status === QuoteStatus.Accepted && q.carrierId === (profile?.userId ?? '')) && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
              <h2 className="font-semibold text-green-900">Job Accepted</h2>
            </div>
            <p className="text-sm text-green-700">You have confirmed this job. Waiting for the customer to complete payment.</p>
          </div>
        )}

        {/* Customer payment result — success/cancelled banners */}
        {isCustomer && paymentResult === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-green-900 text-sm">Payment completed</p>
              <p className="text-xs text-green-700 mt-0.5">Your contract will be emailed to both parties shortly.</p>
            </div>
          </div>
        )}

        {/* Customer payment panel  Cash term, status Accepted(4) or CarrierAccepted(13) */}
        {(job.status === JobStatus.Accepted || job.status === JobStatus.CarrierAccepted) &&
          isCustomer && job.paymentTerm === PaymentTerm.Cash && paymentResult !== 'success' && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            {job.status === JobStatus.Accepted && !job.carrierAcceptedAt ? (
              /* Driver hasn't confirmed yet  customer must wait */
              <>
                <h2 className="font-semibold text-blue-900 mb-1">Waiting for Driver Confirmation</h2>
                <div className="flex items-start gap-3 mt-2">
                  <span className="w-2 h-2 mt-1.5 rounded-full bg-amber-400 animate-pulse shrink-0" />
                  <p className="text-sm text-blue-700">
                    Your quote has been accepted. The driver has been notified and must confirm
                    before you can pay. The payment button will appear here automatically.
                  </p>
                </div>
              </>
            ) : (
              /* Driver confirmed  show Pay Now */
              <>
                <h2 className="font-semibold text-blue-900 mb-1">Payment Required</h2>
                <p className="text-sm text-blue-700 mb-1">
                  {job.status === JobStatus.CarrierAccepted
                    ? 'Your driver has accepted the job. Please pay to confirm the booking.'
                    : 'Your driver has confirmed. Pay now to secure your booking.'}
                </p>
                <p className="text-lg font-bold text-blue-900 mb-3">
                  Total: £{(job.contract?.agreedPrice ?? quotes.find(q => q.status === QuoteStatus.Accepted)?.amount ?? 0).toFixed(2)}
                </p>
                {paymentResult === 'cancelled' && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                    Your previous payment was cancelled. You can try again below.
                  </p>
                )}
                {paymentIntentMutation.isError && (
                  <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                    Payment failed. Please try again or contact support.
                  </p>
                )}
                <button
                  onClick={() => paymentIntentMutation.mutate()}
                  disabled={paymentIntentMutation.isPending}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white
                             py-2.5 rounded-lg font-medium text-sm transition-colors"
                >
                  {paymentIntentMutation.isPending ? 'Redirecting to payment...' : 'Pay Now'}
                </button>
              </>
            )}
          </div>
        )}

        {/* Step 7: Driver pays commission (Credit term) */}
        {job.status === JobStatus.CarrierAccepted && !isCustomer &&
          job.paymentTerm === PaymentTerm.Credit &&
          job.quotes.some(q => q.status === QuoteStatus.Accepted && q.carrierId === (profile?.userId ?? '')) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h2 className="font-semibold text-amber-900 mb-1">Platform Commission Due</h2>
            <p className="text-sm text-amber-700 mb-1">
              This is a Credit-term job. Please pay the platform commission to proceed.
            </p>
            <p className="text-lg font-bold text-amber-900 mb-3">
              Commission: £{job.contract?.commissionAmount?.toFixed(2) ?? '£"'}
            </p>
            <button
              onClick={() => carrierCommissionMutation.mutate()}
              disabled={carrierCommissionMutation.isPending}
              className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 text-white
                         py-2.5 rounded-lg font-medium text-sm transition-colors"
            >
              {carrierCommissionMutation.isPending ? 'Processing...' : 'Pay Commission'}
            </button>
          </div>
        )}

        {/* Employee & Vehicle Assignment — Carrier only. Hidden while awaiting customer payment (Cash: status 4 + carrierAcceptedAt set). */}
        {!isCustomer && job.status >= JobStatus.Accepted && job.status !== JobStatus.Cancelled && job.status !== JobStatus.Rejected &&
          !(job.status === JobStatus.Accepted && !!job.carrierAcceptedAt) && (
          <JobAssignmentPanel jobId={id!} getToken={getToken} />
        )}

        {/* Review section — shown to both parties after payment is released */}
        {job.status === JobStatus.PaymentReleased && !reviewSuccess && (
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <h2 className="font-semibold text-gray-900 mb-1">Leave a Review</h2>
            <p className="text-sm text-gray-500 mb-4">
              {isCustomer ? `Rate the ${isRepairJob ? 'Workshop' : 'Carrier'}` : 'Rate the Customer'}
            </p>
            {reviewError && (
              <p className="mb-3 text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{reviewError}</p>
            )}
            <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  data-rating={star}
                  onClick={() => setReviewRating(star)}
                  aria-label={`${star} star`}
                  className={`text-2xl leading-none transition-colors ${star <= reviewRating ? 'text-amber-400' : 'text-gray-300 hover:text-amber-300'}`}
                >
                  ★
                </button>
              ))}
            </div>
            <textarea
              rows={3}
              placeholder="Comment (optional)"
              value={reviewComment}
              onChange={e => setReviewComment(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none mb-3"
            />
            <button
              onClick={() => handleSubmitReview(
                isCustomer
                  ? (acceptedQuote?.carrierId ?? '')
                  : (job.customerId ?? '')
              )}
              disabled={reviewSubmitting || reviewRating === 0}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold px-6 py-2.5 rounded-lg transition-colors text-sm flex items-center gap-2"
            >
              {reviewSubmitting && <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {reviewSubmitting ? 'Submitting…' : 'Submit Review'}
            </button>
          </div>
        )}
        {reviewSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
            <p className="text-sm text-green-700 font-medium">Review submitted. Thank you!</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  JOB ASSIGNMENT PANEL  (Carrier — assign employee + vehicle)
// ─────────────────────────────────────────────
function JobAssignmentPanel({ jobId, getToken }: { jobId: string; getToken: () => Promise<string> }) {
  const qc = useQueryClient();
  const api = createApiClient(getToken);

  const { data: assignment, isLoading: loadingAssignment } = useQuery({
    queryKey: ['job-assignment', jobId],
    queryFn: () => jobAssignmentService(api).get(jobId),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['carrier-employees'],
    queryFn: () => carrierEmployeesService(api).getAll(),
  });

  const { data: vehicles = [] } = useQuery({
    queryKey: ['carrier-fleet'],
    queryFn: () => vehiclesService(api).getAll(),
  });

  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [selectedVehicle,  setSelectedVehicle]  = useState<string>('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (assignment) {
      setSelectedEmployee(assignment.employeeId ?? '');
      setSelectedVehicle(assignment.vehicleId ?? '');
    }
  }, [assignment]);

  const assignMut = useMutation({
    mutationFn: () => jobAssignmentService(api).assign(jobId, {
      employeeId: selectedEmployee || undefined,
      vehicleId:  selectedVehicle  || undefined,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['job-assignment', jobId] }),
  });

  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-blue-600" />
        <h2 className="font-semibold text-gray-900">Assign Employee &amp; Vehicle</h2>
      </div>

      {loadingAssignment ? (
        <div className="flex justify-center py-4">
          <div className="animate-spin w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="space-y-3">
          {/* Current assignment summary */}
          {(assignment?.employeeName || assignment?.vehicleRegistration) && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-xs text-green-800 space-y-1">
              {assignment?.employeeName && (
                <p>Driver: <span className="font-semibold">{assignment.employeeName}</span></p>
              )}
              {assignment?.vehicleRegistration && (
                <p>Vehicle: <span className="font-semibold">{assignment.vehicleRegistration}{assignment.vehicleMake ? ` — ${assignment.vehicleMake} ${assignment.vehicleModel ?? ''}` : ''}</span></p>
              )}
              {assignment?.magicLinkUrl && (
                <div className="flex items-center gap-2 pt-1">
                  <Link2 className="w-3 h-3 shrink-0" />
                  <span className="truncate flex-1">{assignment.magicLinkUrl}</span>
                  <button
                    onClick={() => handleCopy(assignment.magicLinkUrl!)}
                    className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded bg-green-100 hover:bg-green-200 transition-colors"
                  >
                    {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Employee / Driver</label>
              <select
                value={selectedEmployee}
                onChange={e => setSelectedEmployee(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">— None —</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.fullName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Vehicle</label>
              <select
                value={selectedVehicle}
                onChange={e => setSelectedVehicle(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">— None —</option>
                {vehicles.map(v => (
                  <option key={v.id} value={v.id}>{v.registration}{v.make ? ` — ${v.make} ${v.model ?? ''}` : ''}</option>
                ))}
              </select>
            </div>
          </div>

          <button
            onClick={() => assignMut.mutate()}
            disabled={assignMut.isPending || (!selectedEmployee && !selectedVehicle)}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white py-2 rounded-lg text-sm font-medium transition-colors"
          >
            {assignMut.isPending ? 'Saving…' : 'Save Assignment'}
          </button>

          {employees.length === 0 && vehicles.length === 0 && (
            <p className="text-xs text-gray-400 text-center">
              <a href="/employees" className="text-blue-600 hover:underline">Add employees</a> and <a href="/vehicles" className="text-blue-600 hover:underline">vehicles</a> to assign them here.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}

// 
//  QUOTES SECTION  (enhanced collapsible rows)
// 

function formatBidAge(submittedAt: string): string {
  const diffMs = Date.now() - new Date(submittedAt).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

type QuoteSortKey = 'price_asc' | 'price_desc' | 'rating_desc' | 'jobs_desc';

const QUOTE_SORT_LABELS: Record<QuoteSortKey, string> = {
  price_asc:   'Price: Low → High',
  price_desc:  'Price: High → Low',
  rating_desc: 'Highest Rated',
  jobs_desc:   'Most Experienced',
};

const SORT_PREF_KEY = 'transit_quote_sort_default';
const FAV_KEY       = 'transit_driver_favs';

function QuotesSection({
  quotes,
  jobStatus,
  jobId,
  isCustomer,
  customerId,
  currentUserId,
  profileUserId,
  currentUserName,
  unreadPerCarrier,
  carrierAcceptedAt,
  getToken,
  onAccept,
  onReject,
  actionPending,
  commissionPercent,
}: {
  quotes: QuoteSummaryDto[];
  jobStatus: JobStatus;
  jobId: string;
  isCustomer: boolean;
  customerId: string;
  currentUserId: string;
  profileUserId: string;
  currentUserName: string;
  unreadPerCarrier: { carrierId: string; unreadCount: number }[];
  carrierAcceptedAt?: string | null;
  getToken: () => Promise<string>;
  onAccept: (quoteId: string) => void;
  onReject: (quoteId: string) => void;
  actionPending: boolean;
  commissionPercent?: number;
}) {
  // Build a quick lookup: carrierId ' unreadCount
  const unreadMap = useMemo(
    () => new Map(unreadPerCarrier.map(r => [r.carrierId, r.unreadCount])),
    [unreadPerCarrier]
  );
  //  Sort (persisted) 
  const [sortKey, setSortKey] = useState<QuoteSortKey>(() =>
    (localStorage.getItem(SORT_PREF_KEY) as QuoteSortKey) ?? 'price_asc'
  );
  useEffect(() => { localStorage.setItem(SORT_PREF_KEY, sortKey); }, [sortKey]);

  //  Favorites (persisted) 
  const [favorites, setFavorites] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) ?? '[]')); }
    catch { return new Set(); }
  });
  const toggleFav = (carrierId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = new Set(prev);
      next.has(carrierId) ? next.delete(carrierId) : next.add(carrierId);
      localStorage.setItem(FAV_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  //  Expanded rows 
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleRow = (id: string) =>
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const pendingQuotes = quotes.filter(q => q.status === QuoteStatus.Pending);
  const allExpanded   = pendingQuotes.length > 0 && pendingQuotes.every(q => expanded.has(q.id));
  const toggleAll     = () =>
    setExpanded(allExpanded ? new Set() : new Set(pendingQuotes.map(q => q.id)));

  //  Filters 
  const [showFilters,    setShowFilters]    = useState(false);
  const [filterFavsOnly, setFilterFavsOnly] = useState(false);
  const [filterMinRating, setFilterMinRating] = useState('');
  const [filterMaxPrice,  setFilterMaxPrice]  = useState('');

  const activeFilterCount =
    (filterFavsOnly ? 1 : 0) + (filterMinRating ? 1 : 0) + (filterMaxPrice ? 1 : 0);

  //  Confirmation modal 
  const [confirm, setConfirm] = useState<{
    action: 'accept' | 'reject'; quoteId: string; name: string;
  } | null>(null);

  const handleConfirm = () => {
    if (!confirm) return;
    if (confirm.action === 'accept') onAccept(confirm.quoteId);
    else onReject(confirm.quoteId);
    setConfirm(null);
  };

  //  Sort + filter 
  const displayed = useMemo(() => {
    const minRating  = filterMinRating ? parseFloat(filterMinRating) : 0;
    const maxPrice   = filterMaxPrice  ? parseFloat(filterMaxPrice)  : Infinity;

    return [...quotes]
      .filter(q => {
        if (filterFavsOnly && !favorites.has(q.carrierId)) return false;
        if (q.averageCarrierRating < minRating) return false;
        if (q.amount > maxPrice) return false;
        return true;
      })
      .sort((a, b) => {
        switch (sortKey) {
          case 'price_asc':   return a.amount - b.amount;
          case 'price_desc':  return b.amount - a.amount;
          case 'rating_desc': return b.averageCarrierRating - a.averageCarrierRating;
          case 'jobs_desc':   return b.carrierCompletedJobs - a.carrierCompletedJobs;
        }
      });
  }, [quotes, sortKey, filterFavsOnly, filterMinRating, filterMaxPrice, favorites]);

  const canAct = isCustomer && jobStatus === JobStatus.Quoted;
  // A row can only be expanded by the customer (job poster) or the carrier who placed that specific quote
  const canExpandRow = (quoteCarrierId: string) =>
    isCustomer || quoteCarrierId === profileUserId;

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">

        {/*  Header row  */}
        <div className="px-4 py-3 border-b border-gray-100">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h2 className="font-semibold text-gray-900 text-sm">
              Quotes
              <span className="ml-1.5 text-xs font-normal text-gray-400">
                ({displayed.length}{displayed.length !== quotes.length ? ` of ${quotes.length}` : ''})
              </span>
            </h2>

            <div className="flex items-center gap-1.5 flex-wrap">
              {/* Sort selector */}
              <div className="relative">
                <ArrowUpDown className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
                <select
                  value={sortKey}
                  onChange={e => setSortKey(e.target.value as QuoteSortKey)}
                  className="text-xs pl-6 pr-2 py-1.5 border border-gray-200 rounded-lg bg-white
                             focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none"
                >
                  {(Object.entries(QUOTE_SORT_LABELS) as [QuoteSortKey, string][]).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Filter toggle */}
              <button
                onClick={() => setShowFilters(v => !v)}
                className={`relative flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border transition-colors
                  ${showFilters ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'}`}
              >
                <Filter className="w-3 h-3" /> Filters
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-amber-400 text-blue-900 text-[10px]
                                   font-bold w-4 h-4 rounded-full flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Expand / Collapse all */}
              {pendingQuotes.length > 0 && (
                <button
                  onClick={toggleAll}
                  className="flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg border
                             border-gray-200 bg-white text-gray-600 hover:border-blue-300 transition-colors"
                >
                  {allExpanded
                    ? <><ChevronsUp className="w-3 h-3" /> Collapse All</>
                    : <><ChevronsDown className="w-3 h-3" /> Expand All</>}
                </button>
              )}
            </div>
          </div>
        </div>

        {/*  Filter panel  */}
        {showFilters && (
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Min Rating</label>
              <input
                type="number" min="0" max="5" step="0.1" placeholder="e.g. 4.0"
                value={filterMinRating}
                onChange={e => setFilterMinRating(e.target.value)}
                className="w-24 text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Max Price (£)</label>
              <input
                type="number" min="0" step="1" placeholder="e.g. 200"
                value={filterMaxPrice}
                onChange={e => setFilterMaxPrice(e.target.value)}
                className="w-28 text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <label className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={filterFavsOnly}
                onChange={e => setFilterFavsOnly(e.target.checked)}
                className="rounded"
              />
              Favourites only
            </label>
            {activeFilterCount > 0 && (
              <button
                onClick={() => { setFilterFavsOnly(false); setFilterMinRating(''); setFilterMaxPrice(''); }}
                className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
              >
                <RotateCcw className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        )}

        {/*  Quote rows  */}
        <div className="divide-y divide-gray-100">
          {displayed.length === 0 && (
            <p className="text-center text-xs text-gray-400 py-6">No quotes match your filters.</p>
          )}

          {displayed.map(quote => {
            const isExpanded  = expanded.has(quote.id);
            const isFav       = favorites.has(quote.carrierId);
            const isPending   = quote.status === QuoteStatus.Pending;
            const isAccepted  = quote.status === QuoteStatus.Accepted;
            const isRejected  = quote.status === QuoteStatus.Rejected || quote.status === QuoteStatus.Withdrawn;
            const unreadCount = unreadMap.get(quote.carrierId) ?? 0;
            const bidAge      = formatBidAge(quote.submittedAt);

            return (
              <div key={quote.id} className={isAccepted ? 'bg-green-50' : isRejected ? 'bg-gray-50 opacity-60' : ''}>

                {/*  Collapsed row (always visible)  */}
                <div
                  role={canExpandRow(quote.carrierId) ? 'button' : undefined}
                  onClick={() => canExpandRow(quote.carrierId) && toggleRow(quote.id)}
                  className={`flex items-center gap-2 px-4 py-3 transition-colors select-none ${
                    canExpandRow(quote.carrierId) ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
                  }`}
                >
                  {/* Driver avatar */}
                  <div className="relative shrink-0">
                    <UserAvatar name={quote.carrierName} role="Carrier" size="sm" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold
                                       min-w-[16px] h-4 px-0.5 rounded-full flex items-center justify-center leading-none">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>

                  {/* Favourite */}
                  <button
                    onClick={e => toggleFav(quote.carrierId, e)}
                    className="shrink-0 p-1 rounded-full hover:bg-pink-50 transition-colors"
                    title={isFav ? 'Remove from favourites' : 'Add to favourites'}
                  >
                    <Heart className={`w-4 h-4 ${isFav ? 'fill-pink-500 text-pink-500' : 'text-gray-300'}`} />
                  </button>

                  {/* Driver name + rating + bid age */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900 truncate">{quote.carrierName}</span>
                      {isFav && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pink-100 text-pink-600 font-medium">Favourite</span>
                      )}
                      {isAccepted && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-100 text-green-700 font-semibold">Accepted</span>
                      )}
                      {isRejected && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-500 font-medium">Declined</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="flex items-center gap-0.5 text-xs text-gray-500">
                        <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        {quote.averageCarrierRating.toFixed(1)}
                      </span>
                      <span className="text-xs text-gray-400">Â·</span>
                      <span className="text-xs text-gray-500">{quote.carrierCompletedJobs} jobs</span>
                      <span className="text-xs text-gray-400">Â·</span>
                      <span className="text-xs text-gray-400" title={new Date(quote.submittedAt).toLocaleString('en-GB')}>
                        {bidAge}
                      </span>
                    </div>
                  </div>

                  {/* Amount + unread indicator */}
                  <div className="text-right shrink-0">
                    <p className="text-base font-bold text-gray-900">£{quote.amount.toFixed(2)}</p>
                    {!isCustomer && quote.carrierNetAmount != null && <p className="text-[10px] text-gray-400">net £{quote.carrierNetAmount.toFixed(2)}</p>}
                    {unreadCount > 0 && (
                      <p className="text-[10px] text-rose-500 font-semibold mt-0.5">
                        {unreadCount} new msg{unreadCount > 1 ? 's' : ''}
                      </p>
                    )}
                  </div>

                  {/* Accept / Reject icons (customer only, pending quotes) */}
                  {canAct && isPending && (
                    <div className="flex items-center gap-1 shrink-0 ml-1">
                      <button
                        onClick={e => { e.stopPropagation(); setConfirm({ action: 'accept', quoteId: quote.id, name: quote.carrierName }); }}
                        disabled={actionPending}
                        title="Accept quote"
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-green-100 hover:bg-green-200
                                   text-green-700 disabled:opacity-40 transition-colors"
                      >
                        <Check className="w-4 h-4 stroke-[2.5]" />
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); setConfirm({ action: 'reject', quoteId: quote.id, name: quote.carrierName }); }}
                        disabled={actionPending}
                        title="Reject quote"
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200
                                   text-red-600 disabled:opacity-40 transition-colors"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {/* Expand chevron — only for users who can expand this row */}
                  {canExpandRow(quote.carrierId) && (
                    <span className="shrink-0 text-gray-400 ml-1">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                  )}
                </div>

                {/*  Expanded detail panel  */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 bg-blue-50/40 border-t border-blue-100">
                    {quote.message ? (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-500 mb-1">Driver's message</p>
                        <p className="text-sm text-gray-700 italic bg-white rounded-lg px-3 py-2 border border-gray-100">
                          "{quote.message}"
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic mb-3">No message from driver.</p>
                    )}

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                        <p className="text-gray-400">Quote amount</p>
                        <p className="font-semibold text-gray-900 text-sm">£{quote.amount.toFixed(2)}</p>
                      </div>
                      {!isCustomer && (
                        <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                          <p className="text-gray-400">Platform fee{commissionPercent != null ? ` (${commissionPercent}%)` : ''}</p>
                          <p className="font-semibold text-gray-900 text-sm">£{quote.platformCommission?.toFixed(2) ?? '—'}</p>
                        </div>
                      )}
                      {!isCustomer && (
                        <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                          <p className="text-gray-400">Driver receives</p>
                          <p className="font-semibold text-green-700 text-sm">£{quote.carrierNetAmount?.toFixed(2) ?? '—'}</p>
                        </div>
                      )}
                      <div className="bg-white rounded-lg px-3 py-2 border border-gray-100">
                        <p className="text-gray-400">Quote expires</p>
                        <p className="font-semibold text-gray-900 text-sm">
                          {new Date(quote.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        </p>
                      </div>
                    </div>

                    {quote.isBelowFloor && (
                      <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                         This quote is below the recommended price floor.
                      </p>
                    )}

                    {/* Events timeline  collapsible, default collapsed */}
                    <QuoteEventLog
                      quote={quote}
                      carrierAcceptedAt={isAccepted ? carrierAcceptedAt : undefined}
                    />

                    {/* Live chat thread with this driver */}
                    <div className="mt-3">
                      <MessagingPanel
                        jobId={jobId}
                        jobReference={jobId}
                        currentUserId={currentUserId}
                        currentUserName={currentUserName}
                        getToken={getToken}
                        otherPartyId={isCustomer ? quote.carrierId : customerId}
                        otherPartyName={quote.carrierName}
                      />
                    </div>

                    {canAct && isPending && (
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => setConfirm({ action: 'accept', quoteId: quote.id, name: quote.carrierName })}
                          disabled={actionPending}
                          className="flex-1 flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700
                                     disabled:bg-gray-300 text-white py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          <Check className="w-4 h-4" /> Accept Quote
                        </button>
                        <button
                          onClick={() => setConfirm({ action: 'reject', quoteId: quote.id, name: quote.carrierName })}
                          disabled={actionPending}
                          className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 hover:bg-red-100
                                     border border-red-200 disabled:opacity-40 text-red-700 py-2 rounded-lg text-sm font-medium transition-colors"
                        >
                          <XCircle className="w-4 h-4" /> Reject Quote
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/*  Confirmation modal  */}
      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
              confirm.action === 'accept' ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {confirm.action === 'accept'
                ? <Check className="w-6 h-6 text-green-600" />
                : <XCircle className="w-6 h-6 text-red-600" />}
            </div>
            <h3 className="text-base font-semibold text-gray-900 text-center mb-1">
              {confirm.action === 'accept' ? 'Accept this quote?' : 'Reject this quote?'}
            </h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              {confirm.action === 'accept'
                ? <>You are about to accept <strong>{confirm.name}</strong>'s quote. A contract will be generated and all other quotes will be declined.</>
                : <>You are about to decline <strong>{confirm.name}</strong>'s quote. This cannot be undone.</>}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirm(null)}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={actionPending}
                className={`flex-1 py-2.5 rounded-xl text-sm font-medium text-white transition-colors disabled:opacity-50 ${
                  confirm.action === 'accept'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionPending ? 'Processing' : confirm.action === 'accept' ? 'Yes, Accept' : 'Yes, Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

